import { Grid, Typography } from '@mui/material';
import { ApprovalButton } from '@tassat/button';
import {
    AssetTypes,
    ClientWalletRoleEnum,
    DeclineReason,
    DeclineReasonCodeEnum,
    DeclineReasons,
    TransactionStatusCode,
    TransactionTypeChar
} from '@tassat/common-definitions';
import { ExecutionRecord, OrderRecord } from '@tassat/fred-definitions';
import { formatEpochDate } from '@tassat/text/src/helpers/helpers';
import * as React from 'react';

import Worker from 'Lib/worker/PrimusWorker';
import { muiStyles } from 'SharedComponents/transactionDetails/styles';
import { TransactionDetailsProps } from 'SharedComponents/transactionDetails/types';

import { TransactionHeaderSectionDetails } from './transactionHeaderSectionDetails/transactionHeaderSectionDetails';

export default function TransactionDetails(props: TransactionDetailsProps) {
    const {
        selectedTransaction,
        currentUser,
        currentRole,
        userAccount,
        transactionFirm,
        transactionAccount,
        killSwitch,
        approveTransaction,
        declineTransaction,
        changeModalScreen,
        asset,
        currency,
        history,
        withdrawalData
    } = props;

    const classes = muiStyles(props);
    const isSecurityAssetTxn = asset.Type === AssetTypes.ASSET;
    const [ orderExecutions, setOrderExecutions ] = React.useState<{
        orderId: string;
        executionList: ExecutionRecord[];
    }>({
        orderId: '',
        executionList: []
    });

    React.useEffect(() => {
        if (selectedTransaction.type !== TransactionTypeChar.order) return;

        const getExecutions = async () => {
            try {
                const getOrderParams = {
                    Condition: `RequestId|EQ|${selectedTransaction.RequestId}`,
                    PageNo: 1,
                    RecordPerPage: 10,
                    Sort: 'AuditTime|DESC'
                };
                const orderRes: { data: {
                    orders: OrderRecord[];
                }; } = await Worker.getInstance()
                    .getOrders(getOrderParams);

                const orderId = orderRes.data.orders[0].OrderId;

                const getExecutionsParams = {
                    Condition: `OrderId|EQ|${orderId}`,
                    PageNo: 1,
                    RecordPerPage: 20,
                    Sort: 'ExecutionTime|DESC'
                };

                const executionsRes: { data: {
                    executions: ExecutionRecord[];
                }; } = await Worker.getInstance()
                    .getExecutions(getExecutionsParams);

                setOrderExecutions({
                    orderId,
                    executionList: executionsRes.data.executions
                });
            } catch (error) {
                console.error(error);
            }
        };

        getExecutions();
    }, [selectedTransaction]);

    const declineReason = selectedTransaction.DeclineReason && (
        DeclineReasons.find((reason: DeclineReason) => (
            reason.numericCode === selectedTransaction.DeclineReason ||
            reason.code === String.fromCharCode(selectedTransaction.DeclineReason)
        )) || {}
    ).reason || '';

    const isApprover = React.useMemo(() => {
        if (currentUser?.IsAdmin) {
            if (selectedTransaction.Status === TransactionStatusCode.BANK_NO_ACK) {
                return currentRole.IsTransactionHistoryAllowed;
            }
        }

        return currentUser &&
            !currentUser.IsDeleted &&
            userAccount?.ClientRoleId === ClientWalletRoleEnum.APPROVER &&
            selectedTransaction.RequesterId !== currentUser.UserId &&
            selectedTransaction.Status === TransactionStatusCode.PENDING_APPROVAL;
    }, [
        selectedTransaction,
        currentUser,
        currentRole,
        userAccount
    ]);

    const isDisabled = React.useMemo(() => (
        currentUser && !currentUser.IsAdmin &&
        (currentUser?.IsDeleted || transactionFirm?.IsFrozen || transactionAccount?.IsLocked)
    ), [
        currentUser,
        transactionAccount,
        transactionFirm
    ]);

    const showApprovalButtons = React.useMemo(() => (
        isApprover && !isDisabled
    ), [
        isApprover,
        isDisabled
    ]);

    const showCancelButton = React.useMemo(() => {
        // Currently only clients can cancel, and cannot be locked
        if (currentUser?.IsAdmin || currentUser?.IsLocked) {
            return false;
        }

        // Cannot cancel if firm/account is locked and/or frozen
        if (transactionAccount?.IsLocked || transactionFirm?.IsFrozen || transactionFirm?.IsLocked) {
            return false;
        }

        if (userAccount.ClientRoleId === ClientWalletRoleEnum.VIEW_ONLY || userAccount.ClientRoleId === ClientWalletRoleEnum.NOT_ASSIGNED) {
            return false;
        }

        // If approve/decline buttons show, do not show cancel
        if (showApprovalButtons) return false;

        // Do not show if a decline reason already exists
        if (selectedTransaction.DeclineReason && selectedTransaction.DeclineReason !== DeclineReasonCodeEnum.SUCCESS) {
            return false;
        }

        // FRED-1988: Pending subscriptions/redemptions can be canceled
        // FRED-2000: Pending buy/sells can be canceled as well, so no order side check needed
        // FRED-1159: Partially filled orders can also be canceled
        if (selectedTransaction.type === TransactionTypeChar.order &&
            (
                selectedTransaction.Status === TransactionStatusCode.PENDING ||
                selectedTransaction.Status === TransactionStatusCode.NEW ||
                selectedTransaction.Status === TransactionStatusCode.PARTIAL_FILLED
            )
        ) {
            return true;
        }

        // FRED-3338: Transactions pending approval can be canceled
        // FRED-1990: Orders pending approval can also be canceled
        if (selectedTransaction.Status === TransactionStatusCode.PENDING_APPROVAL &&
            (
                userAccount.ClientRoleId === ClientWalletRoleEnum.MAKER ||
                (
                    userAccount.ClientRoleId === ClientWalletRoleEnum.APPROVER &&
                    selectedTransaction.RequesterId === currentUser.UserId
                )
            )
        ) {
            return true;
        }

        return false;
    }, [
        currentUser,
        selectedTransaction,
        showApprovalButtons,
        transactionFirm,
        transactionAccount,
        userAccount
    ]);

    const isApprovalDisabled = React.useMemo(() => {
        if (killSwitch && selectedTransaction.type !== TransactionTypeChar.send) {
            return true;
        }

        return false;
    }, [ selectedTransaction, killSwitch ]);

    const isDeclineDisabled = React.useMemo(() => {
        if (killSwitch && selectedTransaction.type !== TransactionTypeChar.send) {
            return true;
        }

        return false;
    }, [ selectedTransaction, killSwitch ]);

    return (
        <Grid
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                className={classes.headerContainer}
                container
                direction='row'
                item
                justifyContent='space-between'
                spacing={0}
                wrap='nowrap'
                xs={12}
            >
                <Grid
                    container
                    item
                >
                    <Typography
                        className={classes.headerText}
                        variant='h4'
                    >
                        <strong>TRANSACTION DETAILS</strong>
                    </Typography>
                    <Typography
                        className={classes.headerText}
                        variant='h4'
                    >
                        |
                    </Typography>
                    <Typography
                        className={classes.headerText}
                        id='transactionDetails_auditDateTime_value'
                        variant='h4'
                    >
                        {formatEpochDate(selectedTransaction.AuditTime)}
                    </Typography>
                </Grid>
                <Grid
                    className={classes.buttonsContainer}
                    container
                    justifyContent='flex-end'
                >
                    {showApprovalButtons && (
                        <Grid
                            className={classes.approvalButtonContainer}
                            container
                            direction='row'
                            item
                            justifyContent='flex-end'
                            spacing={0}
                            wrap='nowrap'
                        >
                            <ApprovalButton
                                disabled={isDeclineDisabled}
                                id='transactionDetails_declineTransaction_button'
                                onClick={() => {
                                    declineTransaction(false);
                                }}
                                type='decline'
                            />
                            <ApprovalButton
                                disabled={isApprovalDisabled}
                                id='transactionDetails_approveTransaction_button'
                                onClick={approveTransaction}
                                type='approve'
                            />
                        </Grid>
                    )}
                    {showCancelButton && (
                        <Grid
                            className={classes.approvalButtonContainer}
                            container
                            direction='row'
                            item
                            justifyContent='flex-end'
                            spacing={0}
                            wrap='nowrap'
                        >
                            <ApprovalButton
                                disabled={isDeclineDisabled}
                                id='transactionDetails_cancelTransaction_button'
                                onClick={() => {
                                    declineTransaction(true);
                                }}
                                type='cancel'
                            />
                        </Grid>
                    )}
                    {selectedTransaction.Approver &&
                        ((currentUser.IsAdmin && selectedTransaction.Status !== TransactionStatusCode.BANK_NO_ACK) ||
                            !currentUser.IsAdmin) &&
                            (selectedTransaction.DeclineReason === DeclineReasonCodeEnum.SUCCESS ||
                                selectedTransaction.DeclineReason === DeclineReasonCodeEnum.NONE) && (
                                <Grid
                                    className={classes.approvalButtonContainer}
                                    container
                                    direction='row'
                                    item
                                    justifyContent='flex-end'
                                    spacing={0}
                                    wrap='nowrap'
                                >
                                    <ApprovalButton
                                        id='transactionDetails_transactionApproved_tag'
                                        onClick={() => {
                                        return;
                                    }}
                                        type='approved'
                                    />
                                </Grid>
                    )}
                    {selectedTransaction.Approver &&
                        selectedTransaction.CanceledAt === '0' &&
                        ((currentUser.IsAdmin && selectedTransaction.Status !== TransactionStatusCode.BANK_NO_ACK) ||
                            !currentUser.IsAdmin) &&
                            selectedTransaction.DeclineReason !== DeclineReasonCodeEnum.SUCCESS &&
                            selectedTransaction.DeclineReason !== DeclineReasonCodeEnum.NONE && (
                            <Grid
                                className={classes.approvalButtonContainer}
                                container
                                direction='row'
                                item
                                justifyContent='flex-end'
                                spacing={0}
                                wrap='nowrap'
                            >
                                <ApprovalButton
                                    id='transactionDetails_transactionDeclined_tag'
                                    onClick={() => {
                                    return;
                                }}
                                    type='declined'
                                />
                            </Grid>
                    )}
                    {selectedTransaction.CanceledAt !== '0' && (
                        <Grid
                            className={classes.approvalButtonContainer}
                            container
                            direction='row'
                            item
                            justifyContent='flex-end'
                            spacing={0}
                            wrap='nowrap'
                        >
                            <ApprovalButton
                                id='transactionDetails_transactionDeclined_tag'
                                onClick={() => {
                                    return;
                                }}
                                type='canceled'
                            />
                        </Grid>
                    )}
                </Grid>
            </Grid>
            <TransactionHeaderSectionDetails
                asset={asset}
                currency={currency}
                currentUser={currentUser}
                declineReason={declineReason}
                isSecurityAssetTxn={isSecurityAssetTxn}
                orderExecutions={orderExecutions}
                selectedTransaction={selectedTransaction}
                withdrawalData={withdrawalData}
            />
        </Grid>
    );
}